import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { ButtonModule } from 'primeng/button'
import { finalize } from 'rxjs'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { InputComponent } from 'app/_shared/components/input/input.component'
import { EmailRemindersService } from 'app/_shared/services/email-reminders.service'

@Component({
  selector: 'app-unsubscribe-reminder-coupons',
  imports: [
    ButtonModule,
    FormsModule,
    ButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    TranslatePipe
  ],
  templateUrl: './unsubscribe-reminder-coupons.component.html',
  styleUrl: './unsubscribe-reminder-coupons.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnsubscribeReminderCouponsComponent implements OnInit {
  router = inject(Router)
  unsubscribeService = inject(EmailRemindersService)
  route = inject(ActivatedRoute)
  translate = inject(TranslateService)

  token = signal<string>('')
  reminderType = signal<string>('')
  reason = signal<string>('')
  email = signal<string>('')
  isLoading = signal<boolean>(false)
  isSuccess = signal<boolean>(false)
  isError = signal<boolean>(false)
  errorMessage = signal<string>('')

  unsubscribeDescription = signal<string>('')
  successMessage = signal<string>('')
  validityNote = signal<string>('')
  buttonLabel = signal<string>('')
  unsubscribeForm = new FormGroup({
    email: new FormControl('', {
      validators: [Validators.required, Validators.email],
      nonNullable: true
    }),
    reason: new FormControl('', { nonNullable: true })
  })

  ngOnInit() {
    const tokenParam = this.route.snapshot.paramMap.get('token') || ''
    this.token.set(tokenParam)

    if (!tokenParam) {
      this.isError.set(true)
      this.errorMessage.set(this.translate.instant('UNSUBSCRIBE_REMINDERS.invalidLink'))
      return
    }
    this.fetchReminderType(tokenParam)
  }

  fetchReminderType(token: string) {
    this.unsubscribeService.getReminderTypeByToken(token).subscribe({
      next: response => {
        const type = response.data.type
        this.reminderType.set(type)
        this.populateMessages(type)
      },
      error: () => {
        // this.isError.set(true)
        // this.errorMessage.set(this.translate.instant('UNSUBSCRIBE_REMINDERS.genericError'))
      }
    })
  }

  populateMessages(type: string) {
    const keyMap: Record<string, string> = {
      productKit: 'UNSUBSCRIBE_REMINDERS.productKit',
      giftCard: 'UNSUBSCRIBE_REMINDERS.storeCredit',
      unknown: 'UNSUBSCRIBE_REMINDERS.unknown'
    }

    const baseKey = keyMap[type] ?? 'UNSUBSCRIBE_REMINDERS.unknown'

    this.unsubscribeDescription.set(this.translate.instant(`${baseKey}.description`))

    this.successMessage.set(this.translate.instant(`${baseKey}.successMessage`))

    this.validityNote.set(this.translate.instant(`${baseKey}.validityNote`))

    this.buttonLabel.set(this.translate.instant(`${baseKey}.button`))
  }

  onUnsubscribe(): void {
    if (!this.token()) return

    if (this.reminderType() === 'unknown' && this.unsubscribeForm.invalid) {
      this.unsubscribeForm.markAllAsTouched()
      return
    }

    this.isLoading.set(true)
    this.isError.set(false)

    this.unsubscribeService
      .unsubsribeFromEmailReminders(this.token(), {
        reason: this.unsubscribeForm.controls.reason.value,
        email: this.unsubscribeForm.controls.email.value
      })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: () => {
          this.isSuccess.set(true)
        },
        error: error => {
          this.isError.set(true)
          this.errorMessage.set(
            error?.error?.message || this.translate.instant('UNSUBSCRIBE_REMINDERS.genericError')
          )
        }
      })
  }
}
